import { isRegenAdmin } from '../lib/utils';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { regenApi } from '../lib/api';
import { Loader2, Users, X, ChevronRight } from 'lucide-react';



const fmtEta = (s: number | null | undefined) => {
  if (s == null) return '';
  if (s <= 0) return 'done';
  const m = Math.floor(s / 60), ss = s % 60;
  return m ? `${m}m ${ss}s` : `${ss}s`;
};

/**
 * Global floating monitor: shows every running job's live % + ETA on ANY screen (admin-only).
 * One cheap poll (jobs list). Hides when nothing is running.
 */
export function CreativeTeamMonitor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<any[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [stopping, setStopping] = useState<Record<string, boolean>>({});
  const isAdmin = isRegenAdmin(user?.email);

  const pull = useCallback(async () => {
    if (!isAdmin) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    try { const r = await regenApi.teamJobs(); setJobs((r.data.jobs || []).filter((j: any) => j.status === 'running')); }
    catch { /* transient — stay quiet */ }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    pull();
    const t = setInterval(pull, 8000);
    return () => clearInterval(t);
  }, [isAdmin, pull]);

  if (!isAdmin) return null;
  if (!jobs.length || dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-80 rounded-xl border border-border bg-white dark:bg-gray-800 shadow-xl">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Users className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-gray-900 dark:text-white flex-1">
          {jobs.length} job{jobs.length > 1 ? 's' : ''} generating
        </span>
        <Loader2 className="w-3.5 h-3.5 animate-spin text-green-500" />
        <button onClick={() => setDismissed(true)} className="text-gray-400 hover:text-gray-700"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="px-3 py-2 space-y-2 max-h-64 overflow-auto">
        {jobs.slice(0, 6).map((j: any) => (
          <div key={j.job_id} className="text-[11px]">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-800 dark:text-gray-100 truncate">{j.label}</span>
              <span className="tabular-nums text-gray-500 whitespace-nowrap">{j.progress}% · {fmtEta(j.eta_sec)}</span>
              {/* STOP this generation end-to-end — cooperative cancel: the engine aborts before the
                  next expensive step, so no further credits are spent. */}
              <button
                onClick={() => {
                  // Cooperative cancel: the engine checks between clips, so a clip already rendering
                  // still bills. Be honest about that rather than promising zero spend.
                  if (!window.confirm('Stop this generation? A clip already rendering still bills; no further clips will start.')) return;
                  setStopping(s => ({ ...s, [j.job_id]: true }));
                  regenApi.cancel(j.job_id)
                    .then(() => pull())
                    .catch(() => { setStopping(s => ({ ...s, [j.job_id]: false }));
                                   window.alert('Could not stop it — it may already be finishing.'); });
                }}
                disabled={!!stopping[j.job_id]}
                title="Stop this generation — no further credits spent"
                className="shrink-0 px-1.5 py-0.5 rounded border border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-40">
                {stopping[j.job_id] ? '…' : 'Stop'}
              </button>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden mt-0.5">
              <div className="h-full bg-primary transition-all duration-700" style={{ width: `${j.progress || 0}%` }} />
            </div>
          </div>
        ))}
        <button onClick={() => { navigate('/variation-studio?tab=room'); setTimeout(() => window.location.reload(), 50); }}
          className="w-full mt-1 flex items-center justify-center gap-1 text-xs text-primary hover:underline">
          Open Creative Team Room <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
